import type { AmountMode, IntervalUnit, PaymentKind } from '@klopotun/contracts';

export const PAYMENT_FREQUENCIES = [
  'monthly',
  'quarterly',
  'half_year',
  'yearly',
  'custom',
] as const;

export type PaymentFrequency = (typeof PAYMENT_FREQUENCIES)[number];

export type PaymentFormStep = 'main' | 'history';

export const PAST_PAYMENT_STATE = {
  PAID: 'paid',
  OWED: 'owed',
  SKIPPED: 'skipped',
} as const;

export type PastPaymentState = (typeof PAST_PAYMENT_STATE)[keyof typeof PAST_PAYMENT_STATE];

/** One period already behind today, as the history step shows and edits it. */
export type PastPaymentDraft = {
  dueDate: string;
  state: PastPaymentState;
  /** The amount typed for this period; null follows the amount of the subscription itself. */
  amount: string | null;
  isEdited: boolean;
};

export type PaymentFormValues = {
  name: string;
  amount: string;
  currency: string;
  isVariableAmount: boolean;
  kind: PaymentKind;
  frequency: PaymentFrequency;
  customIntervalUnit: IntervalUnit;
  customIntervalCount: string;
  /** The date of the first charge: its day (and month, for yearly payments) is the anchor. */
  startDate: string;
  /** The periods already behind today, filled in only when the first charge lies in the past. */
  history: PastPaymentDraft[];
  cardId: string;
  categoryId: string;
  endDate: string;
  cancelUrl: string;
  note: string;
};

/**
 * The shape the form hands to `createPaymentBodySchema`. Numbers are nullable because a half typed
 * field has no number in it yet: the contract schema is what turns that into a message, so the
 * form never has to repeat a rule the server already owns.
 */
export type PaymentBodyDraft = {
  name: string;
  kind: PaymentKind;
  amountMode: AmountMode;
  amountMinor: number | null;
  currency: string;
  intervalUnit: IntervalUnit;
  intervalCount: number | null;
  anchorDay: number | null;
  anchorMonth: number | null;
  startDate: string;
  pastPayments: { dueDate: string; amountMinor: number | null }[];
  skippedDates: string[];
  endDate: string | null;
  cardId: string | null;
  categoryId: string | null;
  cancelUrl: string | null;
  note: string | null;
};
